import {dropDisabledNoRendererReads} from "#material/nodes/color/hsv/validation"
import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {color, versionChain} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export interface HsvSockets extends MaterialNodeSockets {
    inputs: {
        color: "color" // found in inputs: color,float; found in settings: color,input; inferred from data: float3,null
        fac: "float" // found in inputs: color; found in settings: float,scalar; inferred from data: float,string,null
        hue: "float" // found in inputs: float,color; found in settings: float,scalar; inferred from data: float,string,null
        saturation: "float" // found in inputs: float; found in settings: float,scalar; inferred from data: float,string,null
        value: "float" // found in inputs: float,color; found in settings: float,scalar; inferred from data: float,string,null
    }
    settings: {
        color: "color" // found in inputs: color,float; found in settings: color,input; inferred from data: float3,null
        fac: "float" // found in inputs: color; found in settings: float,scalar; inferred from data: float,string,null
        hue: "float" // found in inputs: float,color; found in settings: float,scalar; inferred from data: float,string,null
        saturation: "float" // found in inputs: float; found in settings: float,scalar; inferred from data: float,string,null
        value: "float" // found in inputs: float,color; found in settings: float,scalar; inferred from data: float,string,null
    }
    outputs: {
        color: "color"
    }
}

export const hsvDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeHueSaturation,
    },
    nodeClass: "Hsv",
    versionChain: versionChain([dropDisabledNoRendererReads]),
    ui: {
        label: "Hue Saturation Value",
        color: "#8d802f",
        disableable: true,
        items: [
            {name: "color", valueType: "color", label: "Color", output: true},
            {name: "hue", valueType: "float", label: "Hue", defaultValue: 0.5, range: {min: 0, max: 1}, input: true, setting: true},
            {name: "saturation", valueType: "float", label: "Saturation", defaultValue: 1, range: {min: 0, max: 2}, input: true, setting: true},
            {name: "value", valueType: "float", label: "Value", defaultValue: 1, range: {min: 0, max: 2}, input: true, setting: true},
            {name: "fac", valueType: "float", label: "Fac", defaultValue: 1, range: {min: 0, max: 1}, input: true, setting: true},
            {name: "color", valueType: "color", label: "Color", input: true, setting: true, defaultValue: {r: 0.0, g: 0.0, b: 0.0}},
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({color: materialColorOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                color: optionalMaterialInput,
                hue: optionalMaterialInput,
                saturation: optionalMaterialInput,
                value: optionalMaterialInput,
                fac: optionalMaterialInput,
            }),
            settings: z.strictObject({
                color: z.optional(color),
                hue: z.optional(z.number()),
                saturation: z.optional(z.number()),
                value: z.optional(z.number()),
                fac: z.optional(z.number()),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<HsvSockets>
